var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isNil, notNil } from './nil';
import { waitAll } from './promise';
/**
 * In case keyFunction results in duplicate keys for multiple elements, the last one will be inserted to map
 * @param {Item[]} items - input array to convert to map
 * @param {(item: Item) => Key} keyFunction - used to determine the key for the given item
 * @param {(item: Item) => Value} valueFunction - value mapper, defaults to identity function
 * @returns {Map<Key, Item>}
 */
export function arrayToMap(items, keyFunction, valueFunction = (item) => item) {
    return new Map(items.map((item) => [keyFunction(item), valueFunction(item)]));
}
/**
 * Preserves the relative order of input array elements
 * @param {Item[]} items - input array to convert to map
 * @param {(item: Item) => Key} keyFunction - used to determine the key for the given item
 * @param {(item: Item) => Value} valueFunction - value mapper, the default is identity function
 * @returns {Map<Key, Item[]>}
 */
export function arrayToMultiMap(items, keyFunction, valueFunction = (item) => item) {
    return items.reduce((map, item) => {
        const key = keyFunction(item);
        let values = map.get(key);
        if (values === undefined) {
            values = [];
            map.set(key, values);
        }
        values.push(valueFunction(item));
        return map;
    }, new Map());
}
export function getWithDefault(map, key, defaultValue) {
    const item = map.get(key);
    if (item === undefined) {
        return defaultValue;
    }
    return item;
}
/**
 * Groups items in a way that a single group will not contain one item (determined by idFn) twice.
 *
 * Preserves the relative order of two items in the input array -
 * they will be either after one another in the same group or in sequential groups
 * @param list - input to group
 * @param idFn - used to determine the property based on which to group
 */
export function groupsWithoutDuplicates(list, idFn) {
    const groups = [];
    for (const item of list) {
        const itemId = idFn(item);
        let group = findFirstGroupNotContainingItem(groups, itemId);
        if (group === undefined) {
            group = new Map();
            groups.push(group);
        }
        group.set(itemId, item);
    }
    return groups.reduce((memo, group) => {
        memo.push(Array.from(group.values()));
        return memo;
    }, []);
}
function findFirstGroupNotContainingItem(groups, itemId) {
    for (const group of groups) {
        if (!group.has(itemId)) {
            return group;
        }
    }
    return undefined;
}
/**
 * De-duplicates the provided array based on the results from keyFunction.
 * For duplicate elements keeps the last occurrence in the input array.
 */
export function deduplicateLastByKey(items, keyFunction) {
    return unique(items, keyFunction, () => true);
}
/**
 * De-duplicates the provided array based on the results from keyFunction.
 * For duplicate elements keeps the first occurrence in the input array.
 */
export function deduplicateFirstByKey(items, keyFunction) {
    return unique(items, keyFunction);
}
/**
 * De-duplicates the provided array based on the results from keyFunction.
 * For duplicate elements uses the comparator argument, replacing if comparator returns true
 *
 * @param {Item} items
 * @param {(item: Item) => string | number} keyFunction
 * @param {(existingItem: Item, newItem: Item, idx: number) => boolean} [comparator = () => false]
 * @returns {Item[]}
 */
export function unique(items, keyFunction, comparator = () => false) {
    const map = items.reduce((memo, item, idx) => {
        const key = keyFunction(item);
        const existingItem = memo.get(key);
        if (isNil(existingItem)) {
            return memo.set(key, item);
        }
        if (comparator(existingItem, item, idx)) {
            memo.delete(key);
            return memo.set(key, item);
        }
        return memo;
    }, new Map());
    return Array.from(map.values());
}
/**
 * Creates a filter which filters out items with null/undefined properties.
 */
export function createPropertyNotNilFilter(...props) {
    return (item) => props.every((p) => notNil(item[p]));
}
/**
 * Divides the input array into arrays that are at most size in length.
 * Preserves the order of input array elements (they will be in the same order in same chunk or subsequent chunks).
 */
export function chunkArray(array, size) {
    if (size < 1) {
        throw new Error(`Chunk size must be >= 1, but '${size}' provided`);
    }
    const temp = [];
    for (let index = 0; index < array.length; index += size) {
        temp.push(array.slice(index, index + size));
    }
    return temp;
}
export function windowed(array, windowSize = 2, stepSize = 1, partialWindows = false) {
    if (windowSize < 1) {
        throw new Error(`Window size must be >= 1, but '${windowSize}' provided`);
    }
    if (stepSize < 1) {
        throw new Error(`Window step size must be >= 1, but '${stepSize}' provided`);
    }
    const temp = [];
    for (let index = 0; index < array.length; index += stepSize) {
        const to = index + windowSize;
        if (to > array.length) {
            if (!partialWindows) {
                break;
            }
        }
        temp.push(array.slice(index, to));
    }
    return temp;
}
export function findLast(array, predicate) {
    const index = findLastIndex(array, predicate);
    if (index === -1) {
        return undefined;
    }
    return array[index];
}
export function findLastIndex(array, predicate) {
    if (array.length === 0) {
        return -1;
    }
    for (let index = array.length - 1; index >= 0; index -= 1) {
        const result = predicate(array[index], index, array);
        if (result) {
            return index;
        }
    }
    return -1;
}
export function partition(array, predicate) {
    const left = [];
    const right = [];
    for (const v of array) {
        if (predicate(v)) {
            left.push(v);
        }
        else {
            right.push(v);
        }
    }
    return [left, right];
}
/**
 * Removes from `left` all elements that are present in `right`.
 * Duplicate elements in `left` will either all get removed, or will all remain.
 * Uses default equality for comparison.
 *
 * Example: `difference([1, 2, 3, 1, 2], [1, 3])` returns `[2, 2]`
 */
export function difference(left, right) {
    const exclude = new Set(right);
    const result = [];
    for (const element of left) {
        if (!exclude.has(element)) {
            result.push(element);
        }
    }
    return result;
}
/**
 * Returns an array of unique elements that exist in all given iterables
 */
export function intersection(...iterables) {
    return iterables
        .map((iter) => (Array.isArray(iter) ? iter : [...iter]))
        .reduce((intersection, arr, idx) => (idx === 1 ? [...new Set(intersection)] : intersection).filter((item) => arr.includes(item)));
}
export function findMin(items, keyFn = (x) => x) {
    return findBest(items, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS doesn't allow comparing unknown values
    (a, b) => a < b, keyFn);
}
export function findMax(items, keyFn = (x) => x) {
    return findBest(items, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS doesn't allow comparing unknown values
    (a, b) => a > b, keyFn);
}
function findBest(items, isBetter, keyFn) {
    const it = items[Symbol.iterator]();
    let firstNonNil = it.next();
    while (firstNonNil.done !== true && isNil(firstNonNil.value)) {
        firstNonNil = it.next();
    }
    if (firstNonNil.done === true) {
        return null;
    }
    let best = firstNonNil.value;
    let bestKey = keyFn(best);
    for (let next = it.next(); next.done !== true; next = it.next()) {
        if (isNil(next.value)) {
            continue;
        }
        const key = keyFn(next.value);
        if (isBetter(key, bestKey)) {
            best = next.value;
            bestKey = key;
        }
    }
    return best;
}
/**
 * Preserves the relative order of input array elements.
 * Throws if at least one predicate promise was rejected.
 */
export function asyncFilter(items, predicate) {
    return __awaiter(this, void 0, void 0, function* () {
        const filtered = yield waitAll(items.map(predicate));
        return items.filter((_, index) => filtered[index]);
    });
}
//# sourceMappingURL=array.js.map